import React from "react";
import { PetAvatars } from "../../../../components";
import { useUrgentVisitStore } from "../../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { useAppointmentStore } from "../../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useUrgentVisitNavigation } from "../../../../hooks/urgent-visit";
import { UrgentVisitStep } from "../../../../state/urgent-visit";
import {
  useUserStore,
} from "../../../../hooks/redux-hooks/user-store/user.store";
import {
  isTodayInTimeZone, fromUnixToFullDateNum,
} from "../../../../utils/unix-timestamp";
import { ReviewLocationCard } from "./ReviewLocationCard";
import { ReviewScheduleCard } from "./ReviewScheduleCard";
import { ReviewReasonCard } from "./ReviewReasonCard";
import { ReviewContactCard } from "./ReviewContactCard";
import { ReviewPromoCode } from "./ReviewPromoCode";
import AvailableCredits from "../../../containers/BookingReviewContainer/AvailableCredits";
import { formatPetNames } from "../../../../components/AvailableTimeSlots/ExpressAvailableTimeSlots";

export const ReviewStep = () => {
  const {
    selectedPets, selectedSlot, selectedLocationDetails,
    petBookingData, homeLocationId, slotUnavailableMessage,
    setCurrentPetIndex, setSlotUnavailableMessage,
    rescheduleAppointmentId,
  } = useUrgentVisitStore();
  const { appointmentDetails } = useAppointmentStore();
  const { goToStep } = useUrgentVisitNavigation();
  const { user } = useUserStore();

  const isReschedule = !!rescheduleAppointmentId;
  const originalPromoCodes = appointmentDetails?.promoCode;
  const hasOriginalPromos = isReschedule && originalPromoCodes && originalPromoCodes.length > 0;

  if (!selectedSlot) return null;

  const petNames = formatPetNames(
    selectedPets.map((p) => p.name),
  );
  const slotEpoch = Number(selectedSlot.time);
  const timeZone = selectedLocationDetails?.timeZone || "America/Chicago";
  const isToday = isTodayInTimeZone(slotEpoch, timeZone);
  const dateObj = fromUnixToFullDateNum(slotEpoch, timeZone);
  const formattedDate = `${dateObj.month} ${dateObj.day}`;

  const petReasons = selectedPets.map((pet, index) => ({
    petName: pet.name,
    reasons: petBookingData[pet.id]?.selectedReasons ?? [],
    notes: petBookingData[pet.id]?.additionalNotes ?? "",
    petIndex: index,
  }));

  const handleEditTime = () => {
    if (slotUnavailableMessage) {
      setSlotUnavailableMessage("");
    }
    goToStep(UrgentVisitStep.TIME_SELECTION);
  };

  const handleEditPetReason = (petIndex: number) => {
    setCurrentPetIndex(petIndex);
    goToStep(UrgentVisitStep.REASON_SELECTION);
  };

  return (
    <div className="w-full flex flex-col gap-4
      border border-taupe-50 rounded-xl p-4"
    >
      <div className="flex gap-2 justify-between items-center">
        <p className="grenette-pro font-semibold text-lg
          leading-5 text-light-black"
        >
          {`Let's confirm ${petNames} visit at:`}
        </p>
        <PetAvatars
          height={48}
          width={48}
          petDetails={selectedPets}
        />
      </div>

      <ReviewLocationCard
        location={selectedLocationDetails}
        homeLocationName={user?.location?.locationName}
        homeLocationId={homeLocationId || ""}
      />

      <ReviewScheduleCard
        slot={selectedSlot}
        formattedDate={formattedDate}
        isToday={isToday}
        weekday={dateObj.weekday}
        errorMessage={slotUnavailableMessage || undefined}
        onEdit={handleEditTime}
      />

      <ReviewReasonCard
        petReasons={petReasons}
        onEditPet={handleEditPetReason}
      />

      {user && (
        <ReviewContactCard
          name={`${user.firstName} ${user.lastName}`}
          phone={user.phone || ""}
          email={user.email}
        />
      )}

      {(!isReschedule || hasOriginalPromos) && (
        <>
          <hr className="border-gray-3 m-0" />
          <ReviewPromoCode
            readOnlyPromoCodes={hasOriginalPromos ? originalPromoCodes : undefined}
          />
        </>
      )}

      <hr className="border-gray-3 m-0" />

      <AvailableCredits />
    </div>
  );
};
